import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Megaphone, Facebook, Instagram, Sparkles, Loader2, Image as ImageIcon, Send, CalendarClock,
  CheckCircle2, XCircle, RefreshCw, Trash2, AlertTriangle, ShieldCheck, Lock,
} from "lucide-react";

const PLATFORM_META = {
  facebook: { label: "Facebook Page", icon: Facebook, color: "text-[#1877F2]" },
  instagram: { label: "Instagram Business", icon: Instagram, color: "text-[#E1306C]" },
};

function ConnectionHub({ status, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/marketing/meta/authorize-url");
      // Open Ayrshare hosted panel in a new tab so the user can complete OAuth
      // there. When they return + refresh, `linked` will reflect their new socials.
      window.open(data.url, "_blank", "noopener,noreferrer");
      setTimeout(onRefresh, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cannot start Ayrshare connect flow");
    } finally {
      setBusy(false);
    }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect from Ayrshare? Scheduled posts will fail until you reconnect.")) return;
    await api.delete("/marketing/meta/disconnect");
    toast.success("Ayrshare connection removed");
    onRefresh();
  };

  const linked = status?.linked || [];
  const linkedFB = linked.includes("facebook");
  const linkedIG = linked.includes("instagram");

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="marketing-connection-hub">
      <div className="bg-white border border-sg-line rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Facebook className="w-6 h-6 text-[#1877F2]" />
          <Instagram className="w-6 h-6 text-[#E1306C]" />
          <div>
            <div className="text-sm font-semibold text-sg-navy">Facebook &amp; Instagram — via Ayrshare</div>
            <div className="text-xs text-sg-mute">Ayrshare aggregates the OAuth handshake so you connect once and publish anywhere.</div>
          </div>
        </div>

        {!status?.configured && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="meta-not-configured">
            <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-900">
              <div className="font-semibold">Ayrshare API key not configured</div>
              <div className="mt-1">Add <code className="font-mono bg-white px-1 rounded">AYRSHARE_API_KEY</code> to the backend <code>.env</code> and reload. Multi-tenant setups also need <code className="font-mono bg-white px-1 rounded">AYRSHARE_DOMAIN</code> + <code className="font-mono bg-white px-1 rounded">AYRSHARE_PRIVATE_KEY</code>.</div>
            </div>
          </div>
        )}

        {status?.configured && !status?.connected && (
          <Button
            type="button"
            onClick={connect}
            disabled={busy}
            className="rounded-full h-11 px-5 bg-sg-navy hover:bg-sg-navy-700 text-white disabled:opacity-50"
            data-testid="meta-connect-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Facebook className="w-4 h-4 mr-2" />}
            {status?.multi_tenant ? "Open Ayrshare connect panel" : "Open Ayrshare dashboard"}
          </Button>
        )}

        {status?.configured && status?.connected && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span className="text-sm text-sg-navy font-medium">
                Ayrshare profile connected · {linked.length} network{linked.length === 1 ? "" : "s"} linked
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2" data-testid="meta-connected-pages">
              <div className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${linkedFB ? "bg-emerald-50 border-emerald-200" : "bg-sg-cream-soft border-sg-line"}`}>
                <Facebook className="w-4 h-4 text-[#1877F2]" />
                <span className="text-xs font-medium text-sg-navy flex-1">Facebook Page</span>
                {linkedFB ? <Badge className="text-[10px] bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-full">Linked</Badge> : <span className="text-[10px] text-sg-mute">Not linked</span>}
              </div>
              <div className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${linkedIG ? "bg-emerald-50 border-emerald-200" : "bg-sg-cream-soft border-sg-line"}`}>
                <Instagram className="w-4 h-4 text-[#E1306C]" />
                <span className="text-xs font-medium text-sg-navy flex-1">Instagram Business</span>
                {linkedIG ? <Badge className="text-[10px] bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-full">Linked</Badge> : <span className="text-[10px] text-sg-mute">Not linked</span>}
              </div>
            </div>
            {status?.profile_key_prefix && (
              <div className="mt-3 text-[10px] font-mono text-sg-mute">Profile · {status.profile_key_prefix}</div>
            )}
            <div className="mt-4 flex items-center gap-3">
              <button type="button" onClick={connect} className="text-xs text-sg-gold-deep hover:text-sg-navy inline-flex items-center gap-1" data-testid="meta-manage-btn">
                <RefreshCw className="w-3 h-3" /> Manage in Ayrshare
              </button>
              <button
                type="button"
                onClick={disconnect}
                className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1"
                data-testid="meta-disconnect-btn"
              >
                <Trash2 className="w-3 h-3" /> Disconnect
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-sg-cream-soft border border-sg-line rounded-2xl p-6">
        <div className="overline text-sg-gold-deep mb-2">DAILY POSTING CAPS</div>
        <div className="text-sm text-sg-navy">
          To protect your accounts from rate limits and buyer fatigue, the studio enforces:
        </div>
        <ul className="mt-3 space-y-2 text-sm text-sg-navy">
          <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-sg-gold-deep" /> Max <b>1 standard post per day</b> per Ayrshare profile.</li>
          <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-sg-gold-deep" /> Max <b>2 review highlights per day</b> per Ayrshare profile.</li>
        </ul>
        <p className="text-xs text-sg-mute mt-3">
          Attempts to publish or schedule beyond the cap return HTTP 429 — the calendar tab greys out overbooked days automatically.
        </p>
      </div>
    </div>
  );
}

function CreativeWorkspace({ status, candidates, templates, branding, onDraftCreated }) {
  const [sourceKind, setSourceKind] = useState("listing");
  const [sourceId, setSourceId] = useState("");
  const [templateId, setTemplateId] = useState("std_post_square");
  const [kind, setKind] = useState("post");
  const [platforms, setPlatforms] = useState(["facebook"]);
  const [extra, setExtra] = useState("");

  const [copyLoading, setCopyLoading] = useState(false);
  const [graphicLoading, setGraphicLoading] = useState(false);
  const [headline, setHeadline] = useState("");
  const [body, setBody] = useState("");
  const [hashtags, setHashtags] = useState([]);
  const [graphicUrl, setGraphicUrl] = useState(null);

  const sourceOptions = useMemo(() => {
    if (sourceKind === "listing") {
      return (candidates.listings || []).map((l) => ({
        value: l.listing_id,
        label: `${l.bhk || "?"} · ${l.property_type || "Apartment"} · ${l.locality || "?"} · ${l.listing_id}`,
        image: l.image_urls?.[0],
      }));
    }
    return (candidates.master_projects || []).map((p) => ({
      value: p.project_id,
      label: `${p.project_name} · ${p.locality_tag} · ${p.developer || "?"}`,
      image: p.image_urls?.[0] || p.thumbnail_url,
    }));
  }, [candidates, sourceKind]);

  const filteredTemplates = useMemo(
    () => (templates || []).filter((t) => t.kind === kind),
    [templates, kind]
  );

  useEffect(() => {
    if (filteredTemplates.length && !filteredTemplates.find((t) => t.template_id === templateId)) {
      setTemplateId(filteredTemplates[0].template_id);
    }
  }, [filteredTemplates, templateId]);

  const togglePlatform = (p) => {
    setPlatforms((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  };

  const genCopy = async () => {
    if (!sourceId) return toast.error("Pick a listing or Master Project first");
    setCopyLoading(true);
    try {
      const { data } = await api.post("/marketing/generate-copy", {
        kind, source_kind: sourceKind, source_id: sourceId,
        extra_instructions: extra || null,
      });
      setHeadline(data.headline);
      setBody(data.body);
      setHashtags(data.hashtags);
      toast.success(`Copy generated with ${data.model}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Copy generation failed");
    } finally {
      setCopyLoading(false);
    }
  };

  const genGraphic = async () => {
    if (!sourceId) return toast.error("Pick a listing or Master Project first");
    setGraphicLoading(true);
    try {
      const { data } = await api.post("/marketing/generate-graphic", {
        template_id: templateId,
        source_kind: sourceKind,
        source_id: sourceId,
        headline: headline || "",
      });
      setGraphicUrl(data.graphic_url);
      toast.success("Graphic composed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Graphic generation failed");
    } finally {
      setGraphicLoading(false);
    }
  };

  const saveDraft = async () => {
    if (!sourceId || !graphicUrl) return toast.error("Generate a graphic first");
    if (!platforms.length) return toast.error("Pick at least one target platform");
    try {
      const { data } = await api.post("/marketing/drafts", {
        kind, source_kind: sourceKind, source_id: sourceId,
        template_id: templateId, headline, body, hashtags,
        graphic_url: graphicUrl, target_platforms: platforms,
      });
      toast.success("Draft saved — head to Review & Schedule to approve.");
      onDraftCreated(data);
      setHeadline(""); setBody(""); setHashtags([]); setGraphicUrl(null); setExtra("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save draft");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6" data-testid="marketing-creative-workspace">
      {/* Controls column */}
      <div className="bg-white border border-sg-line rounded-2xl p-6 space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-wide text-sg-mute">Post kind</Label>
          <div className="mt-2 flex gap-2">
            {[
              { v: "post", label: "Standard Post" },
              { v: "review", label: "Review Highlight" },
            ].map((k) => (
              <button
                key={k.v}
                type="button"
                onClick={() => setKind(k.v)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  kind === k.v
                    ? "bg-sg-navy text-white"
                    : "bg-sg-cream-soft text-sg-navy border border-sg-line hover:border-sg-gold"
                }`}
                data-testid={`mkt-kind-${k.v}`}
              >
                {k.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide text-sg-mute">Source</Label>
          <div className="mt-2 flex gap-2 mb-2">
            <button
              type="button"
              onClick={() => { setSourceKind("listing"); setSourceId(""); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium ${sourceKind === "listing" ? "bg-sg-navy text-white" : "bg-sg-cream-soft text-sg-navy border border-sg-line"}`}
              data-testid="mkt-source-listing"
            >
              My Listing
            </button>
            <button
              type="button"
              onClick={() => { setSourceKind("master_project"); setSourceId(""); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium ${sourceKind === "master_project" ? "bg-sg-navy text-white" : "bg-sg-cream-soft text-sg-navy border border-sg-line"}`}
              data-testid="mkt-source-mp"
            >
              Master Project
            </button>
          </div>
          <Select value={sourceId} onValueChange={setSourceId}>
            <SelectTrigger className="rounded-xl bg-white border-sg-line" data-testid="mkt-source-select">
              <SelectValue placeholder={`Choose a ${sourceKind === "listing" ? "listing" : "master project"}…`} />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {sourceOptions.length === 0 && (
                <div className="px-3 py-2 text-xs text-sg-mute">No candidates available.</div>
              )}
              {sourceOptions.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`mkt-source-opt-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide text-sg-mute">Template</Label>
          <Select value={templateId} onValueChange={setTemplateId}>
            <SelectTrigger className="mt-2 rounded-xl bg-white border-sg-line" data-testid="mkt-template-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {filteredTemplates.map((t) => (
                <SelectItem key={t.template_id} value={t.template_id}>
                  {t.name} · {t.size.join("x")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide text-sg-mute">Target platforms</Label>
          <div className="mt-2 flex gap-2">
            {Object.entries(PLATFORM_META).map(([k, meta]) => {
              const Icon = meta.icon;
              const active = platforms.includes(k);
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => togglePlatform(k)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? "bg-sg-navy text-white border-sg-navy" : "bg-white text-sg-navy border-sg-line hover:border-sg-gold"}`}
                  data-testid={`mkt-platform-${k}`}
                >
                  <Icon className={`w-3.5 h-3.5 ${active ? "" : meta.color}`} />
                  {meta.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide text-sg-mute">Extra instructions <span className="normal-case text-sg-mute">(optional)</span></Label>
          <Textarea
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            rows={2}
            placeholder="e.g. Emphasize investment yield, no emoji, mention Diwali offer"
            className="mt-2 rounded-xl bg-white border-sg-line resize-none"
            data-testid="mkt-extra"
          />
        </div>

        <div className="flex gap-2 flex-wrap">
          <Button type="button" onClick={genCopy} disabled={copyLoading || !sourceId} className="rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white" data-testid="mkt-gen-copy">
            {copyLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {copyLoading ? "Generating…" : "Generate copy (AI)"}
          </Button>
          <Button type="button" onClick={genGraphic} disabled={graphicLoading || !sourceId} className="rounded-full bg-sg-gold-deep hover:bg-sg-gold text-white" data-testid="mkt-gen-graphic">
            {graphicLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImageIcon className="w-4 h-4 mr-2" />}
            {graphicLoading ? "Composing…" : "Compose graphic"}
          </Button>
        </div>

        <div className="pt-3 border-t border-sg-line space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-sg-mute">Headline</Label>
            <Input value={headline} onChange={(e) => setHeadline(e.target.value)} className="mt-1 rounded-xl bg-white border-sg-line" data-testid="mkt-headline" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-sg-mute">Body</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} className="mt-1 rounded-xl bg-white border-sg-line resize-none" data-testid="mkt-body" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-sg-mute">Hashtags</Label>
            <Input
              value={hashtags.join(" ")}
              onChange={(e) => setHashtags(e.target.value.split(/\s+/).filter(Boolean))}
              className="mt-1 rounded-xl bg-white border-sg-line font-mono text-xs"
              placeholder="#PuneRealEstate #BanerHomes …"
              data-testid="mkt-hashtags"
            />
          </div>
          <Button type="button" onClick={saveDraft} disabled={!graphicUrl || !platforms.length} className="w-full rounded-full h-11 bg-sg-navy hover:bg-sg-navy-700 text-white disabled:opacity-50" data-testid="mkt-save-draft">
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Save to Pending Review
          </Button>
        </div>
      </div>

      {/* Preview column */}
      <div className="bg-sg-cream-soft border border-sg-line rounded-2xl p-6">
        <div className="overline text-sg-gold-deep mb-3">LIVE PREVIEW</div>
        {graphicUrl ? (
          <img src={graphicUrl} alt="preview" className="w-full rounded-xl border border-sg-line" data-testid="mkt-preview-img" />
        ) : (
          <div className="w-full aspect-square rounded-xl border-2 border-dashed border-sg-line flex flex-col items-center justify-center text-center p-6 text-sg-mute" data-testid="mkt-preview-empty">
            <ImageIcon className="w-8 h-8 mb-3 opacity-40" />
            <div className="text-sm">Compose a graphic to preview here.</div>
            <div className="text-xs mt-1">Your branding ({branding?.name || "…"}) will be baked into the footer automatically.</div>
          </div>
        )}
        {(headline || body || hashtags.length > 0) && (
          <div className="mt-4 bg-white border border-sg-line rounded-xl p-4 space-y-2" data-testid="mkt-preview-caption">
            {headline && <div className="text-sm font-semibold text-sg-navy">{headline}</div>}
            {body && <div className="text-sm text-sg-navy whitespace-pre-wrap">{body}</div>}
            {hashtags.length > 0 && (
              <div className="text-xs text-sg-gold-deep font-mono">{hashtags.join(" ")}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ReviewSchedule({ status, drafts, onRefresh }) {
  const [publishing, setPublishing] = useState(null); // draft_id
  const [scheduleFor, setScheduleFor] = useState({}); // {draft_id: {when}}
  const [editing, setEditing] = useState(null);

  const linked = status?.linked || [];

  const setSched = (id, patch) => setScheduleFor((s) => ({ ...s, [id]: { ...(s[id] || {}), ...patch } }));

  const publish = async (d) => {
    setPublishing(d.draft_id);
    try {
      await api.post(`/marketing/drafts/${d.draft_id}/publish`, {});
      toast.success("Published!");
      onRefresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Publish failed";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setPublishing(null);
    }
  };

  const schedule = async (d) => {
    const cfg = scheduleFor[d.draft_id];
    if (!cfg?.when) return toast.error("Pick a future date/time");
    setPublishing(d.draft_id);
    try {
      const iso = new Date(cfg.when).toISOString();
      await api.post(`/marketing/drafts/${d.draft_id}/schedule`, { scheduled_at: iso });
      toast.success("Scheduled");
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Schedule failed");
    } finally {
      setPublishing(null);
    }
  };

  const del = async (d) => {
    if (!window.confirm("Delete this draft?")) return;
    await api.delete(`/marketing/drafts/${d.draft_id}`);
    toast.success("Draft deleted");
    onRefresh();
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.patch(`/marketing/drafts/${editing.draft_id}`, {
        headline: editing.headline,
        body: editing.body,
        hashtags: editing.hashtags,
      });
      toast.success("Draft updated");
      setEditing(null);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const statusStyle = {
    draft: "bg-sg-cream-soft text-sg-navy border border-sg-line",
    approved: "bg-blue-100 text-blue-800 border border-blue-200",
    scheduled: "bg-amber-100 text-amber-900 border border-amber-200",
    published: "bg-emerald-100 text-emerald-900 border border-emerald-200",
    failed: "bg-red-100 text-red-800 border border-red-200",
  };

  return (
    <div className="space-y-4" data-testid="marketing-review-list">
      {drafts.length === 0 && (
        <div className="text-center py-12 bg-white border border-sg-line rounded-2xl text-sg-mute" data-testid="mkt-review-empty">
          <Megaphone className="w-8 h-8 mx-auto mb-3 opacity-40" />
          <div className="text-sm">No drafts yet — create one from Creative Workspace.</div>
        </div>
      )}
      {drafts.map((d) => (
        <div key={d.draft_id} className="bg-white border border-sg-line rounded-2xl p-4" data-testid={`mkt-draft-${d.draft_id}`}>
          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,150px)_minmax(0,1fr)] gap-4">
            {d.graphic_url ? (
              <img src={d.graphic_url} alt="draft" className="w-full aspect-square object-cover rounded-xl border border-sg-line" />
            ) : (
              <div className="w-full aspect-square bg-sg-cream-soft rounded-xl" />
            )}
            <div className="min-w-0">
              <div className="flex flex-wrap gap-2 items-center mb-2">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide font-semibold ${statusStyle[d.status] || statusStyle.draft}`}>
                  {d.status}
                </span>
                <span className="text-[10px] text-sg-mute uppercase tracking-wide font-mono">{d.kind}</span>
                {(d.target_platforms || []).map((p) => {
                  const Icon = PLATFORM_META[p]?.icon;
                  return Icon ? <Icon key={p} className={`w-3.5 h-3.5 ${PLATFORM_META[p].color}`} /> : null;
                })}
              </div>
              {d.headline && <div className="text-sm font-semibold text-sg-navy">{d.headline}</div>}
              {d.body && <div className="text-xs text-sg-navy mt-1 line-clamp-3 whitespace-pre-wrap">{d.body}</div>}
              {(d.hashtags || []).length > 0 && (
                <div className="text-[10px] text-sg-gold-deep font-mono mt-1 truncate">{d.hashtags.join(" ")}</div>
              )}

              {d.status === "draft" && (
                <div className="mt-3 pt-3 border-t border-sg-line grid grid-cols-1 lg:grid-cols-[minmax(0,220px)_auto_auto_auto] gap-2 items-end">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wide text-sg-mute">Schedule for</Label>
                    <Input
                      type="datetime-local"
                      value={scheduleFor[d.draft_id]?.when || ""}
                      onChange={(e) => setSched(d.draft_id, { when: e.target.value })}
                      className="mt-1 h-9 rounded-lg bg-white border-sg-line text-xs"
                      data-testid={`mkt-draft-when-${d.draft_id}`}
                    />
                  </div>
                  <Button type="button" onClick={() => publish(d)} disabled={publishing === d.draft_id || !linked.length} className="h-9 rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white text-xs" data-testid={`mkt-draft-publish-${d.draft_id}`}>
                    {publishing === d.draft_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />} Publish now
                  </Button>
                  <Button type="button" onClick={() => schedule(d)} disabled={publishing === d.draft_id || !linked.length} className="h-9 rounded-full bg-sg-gold-deep hover:bg-sg-gold text-white text-xs" data-testid={`mkt-draft-schedule-${d.draft_id}`}>
                    <CalendarClock className="w-3 h-3 mr-1" /> Schedule
                  </Button>
                  <div className="flex gap-1 justify-end">
                    <button type="button" onClick={() => setEditing({ ...d })} className="text-xs text-sg-navy hover:text-sg-gold-deep px-2" data-testid={`mkt-draft-edit-${d.draft_id}`}>
                      Edit
                    </button>
                    <button type="button" onClick={() => del(d)} className="text-xs text-red-600 hover:text-red-800 px-2" data-testid={`mkt-draft-del-${d.draft_id}`}>
                      Delete
                    </button>
                  </div>
                  {!linked.length && (
                    <div className="col-span-full text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
                      Connect Ayrshare in the Connection Hub before publishing.
                    </div>
                  )}
                </div>
              )}

              {d.status === "scheduled" && (
                <div className="mt-3 text-xs text-sg-mute">
                  <CalendarClock className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                  Scheduled for {new Date(d.scheduled_at).toLocaleString()}
                </div>
              )}

              {d.status === "published" && (
                <div className="mt-3 text-xs text-emerald-800">
                  <CheckCircle2 className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                  Published {new Date(d.published_at).toLocaleString()}
                  {d.external_post_ids && Object.entries(d.external_post_ids).map(([platform, id]) => (
                    <span key={platform} className="ml-2 font-mono text-[10px]">· {platform}:{id}</span>
                  ))}
                </div>
              )}

              {d.status === "failed" && d.error && (
                <div className="mt-3 text-xs text-red-800 flex items-start gap-1">
                  <XCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span className="break-all">{d.error}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* -------- Edit modal -------- */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-lg bg-white p-6 rounded-2xl" data-testid="mkt-edit-dialog">
          {editing && (
            <>
              <div className="overline text-sg-gold-deep">EDIT DRAFT</div>
              <h2 className="font-display text-2xl text-sg-navy mt-1">{editing.draft_id}</h2>
              <div className="mt-4 space-y-3">
                <div>
                  <Label className="text-xs uppercase text-sg-mute">Headline</Label>
                  <Input value={editing.headline || ""} onChange={(e) => setEditing((s) => ({ ...s, headline: e.target.value }))} className="mt-1 rounded-xl bg-white border-sg-line" data-testid="mkt-edit-headline" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-sg-mute">Body</Label>
                  <Textarea value={editing.body || ""} onChange={(e) => setEditing((s) => ({ ...s, body: e.target.value }))} rows={6} className="mt-1 rounded-xl bg-white border-sg-line resize-none" data-testid="mkt-edit-body" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-sg-mute">Hashtags</Label>
                  <Input
                    value={(editing.hashtags || []).join(" ")}
                    onChange={(e) => setEditing((s) => ({ ...s, hashtags: e.target.value.split(/\s+/).filter(Boolean) }))}
                    className="mt-1 rounded-xl bg-white border-sg-line font-mono text-xs"
                    data-testid="mkt-edit-hashtags"
                  />
                </div>
              </div>
              <div className="mt-5 flex items-center justify-end gap-3">
                <button type="button" onClick={() => setEditing(null)} className="text-sm text-sg-mute hover:text-sg-navy px-3 py-2">Cancel</button>
                <Button type="button" onClick={saveEdit} className="rounded-full h-10 px-5 bg-sg-navy hover:bg-sg-navy-700 text-white" data-testid="mkt-edit-save">
                  Save changes
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function MarketingStudio() {
  const { user } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();

  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [candidates, setCandidates] = useState({ listings: [], master_projects: [] });
  const [branding, setBranding] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [tab, setTab] = useState("workspace");
  const [gate, setGate] = useState(null); // {message} → subscription lock

  const load = async () => {
    try {
      const [s, t, c, b, d] = await Promise.all([
        api.get("/marketing/meta/status"),
        api.get("/marketing/templates"),
        api.get("/marketing/candidates"),
        api.get("/marketing/branding"),
        api.get("/marketing/drafts"),
      ]);
      setStatus(s.data);
      setTemplates(t.data.templates);
      setCandidates(c.data);
      setBranding(b.data);
      setDrafts(d.data.drafts || []);
    } catch (e) {
      if (e?.response?.status === 402) {
        setGate({ message: e.response.data.detail });
      }
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const q = new URLSearchParams(loc.search);
    if (q.get("meta") === "connected") { toast.success("Meta account connected"); setTab("workspace"); navigate(loc.pathname, { replace: true }); load(); }
    if (q.get("meta") === "error") { toast.error(q.get("reason") ? `Meta OAuth error: ${q.get("reason")}` : "Meta OAuth failed"); navigate(loc.pathname, { replace: true }); }
  }, [loc.search]);

  if (gate) {
    return (
      <DashboardShell>
        <div className="max-w-2xl mx-auto text-center py-20" data-testid="mkt-sub-gate">
          <Lock className="w-10 h-10 text-sg-gold-deep mx-auto mb-4" />
          <h1 className="font-display text-4xl text-sg-navy">Marketing Studio is Pro-only.</h1>
          <p className="text-sg-mute mt-3">{gate.message}</p>
          <Button onClick={() => navigate("/subscribe")} className="mt-6 rounded-full h-11 px-6 bg-sg-navy hover:bg-sg-navy-700 text-white" data-testid="mkt-gate-subscribe">
            Activate ₹999 subscription
          </Button>
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell>
      <div className="flex items-center gap-3 mb-2">
        <Megaphone className="w-6 h-6 text-sg-gold-deep" />
        <div className="overline text-sg-gold-deep">MARKETING STUDIO</div>
      </div>
      <h1 className="font-display text-4xl lg:text-5xl text-sg-navy">
        {user?.role === "owner" ? "Platform-wide campaigns," : "Your listings, "}
        <span className="font-serif-italic text-sg-gold-deep">amplified.</span>
      </h1>
      <p className="text-sg-mute mt-2 max-w-2xl">
        Generate captions with GPT-5.4, overlay your MahaCRITI branding on curated templates, and publish
        to Facebook Business + Instagram Business — all within Meta&apos;s daily volume caps.
      </p>

      <Tabs value={tab} onValueChange={setTab} className="mt-8" data-testid="mkt-tabs">
        <TabsList className="bg-sg-cream-soft border border-sg-line rounded-full p-1 inline-flex">
          <TabsTrigger value="connections" className="rounded-full data-[state=active]:bg-sg-navy data-[state=active]:text-white px-4 py-2 text-sm" data-testid="mkt-tab-connections">
            Connection Hub
          </TabsTrigger>
          <TabsTrigger value="workspace" className="rounded-full data-[state=active]:bg-sg-navy data-[state=active]:text-white px-4 py-2 text-sm" data-testid="mkt-tab-workspace">
            Creative Workspace
          </TabsTrigger>
          <TabsTrigger value="review" className="rounded-full data-[state=active]:bg-sg-navy data-[state=active]:text-white px-4 py-2 text-sm" data-testid="mkt-tab-review">
            Review & Schedule
            {drafts.filter((d) => d.status === "draft").length > 0 && (
              <span className="ml-2 text-[10px] bg-sg-gold text-sg-navy rounded-full px-1.5 font-mono">
                {drafts.filter((d) => d.status === "draft").length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="connections" className="mt-6">
          <ConnectionHub status={status} onRefresh={load} />
        </TabsContent>

        <TabsContent value="workspace" className="mt-6">
          <CreativeWorkspace
            status={status}
            candidates={candidates}
            templates={templates}
            branding={branding}
            onDraftCreated={() => { load(); setTab("review"); }}
          />
        </TabsContent>

        <TabsContent value="review" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-sg-mute">{drafts.length} draft(s) · caps: 1 post + 2 reviews per Page per day</div>
            <button type="button" onClick={load} className="text-xs text-sg-gold-deep hover:text-sg-navy inline-flex items-center gap-1" data-testid="mkt-review-refresh">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>
          <ReviewSchedule status={status} drafts={drafts} onRefresh={load} />
        </TabsContent>
      </Tabs>
    </DashboardShell>
  );
}
